/**
 * "Confirmed → Schedule" — the booking modal for a final round.
 *
 * Split out of FinalRoundModals.tsx (500-line limit) when the DEBRIEF picker was
 * added. The debrief used to be placed silently: the server took the first
 * quarter hour where everyone happened to be free and put a meeting there. TA
 * asked to confirm that time like any other — and, when nothing is free, to see
 * whose meeting would have to move for it, which is the same question the chain
 * options answer for the round itself.
 *
 * The final agenda then goes to everyone involved: the panel as DMs (editable,
 * because a hiring manager or department lead often wants the FYI) or one Slack
 * channel. Any meeting that must move for the debrief is named in it.
 */
import { useEffect, useState } from 'react';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { RadioGroup, RadioItem } from '@finn-auto/design-system/atoms/radio';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { Spinner } from '@finn-auto/design-system/atoms/spinner';
import { fetchDebriefOptions, type DebriefOption } from '../../services/taFinalRoundApi';
import type { ChainOption, FinalRoundMode, RoleBoard, SchedulingCandidate } from '../../types/taActionBoard';
import { panelPeople } from './AskPanelBody';
import type { BookPayload } from './FinalRoundModals';
import {
  COLORS,
  firstNameFromEmail,
  fmtSlotRange,
  fmtTime,
  fullNameFromEmail,
  slotNumber,
} from './format';
import { ModalActions } from './ModalActions';
import { audienceReady, PanelRecipientsField, type PanelAudience } from './PanelRecipients';

export function BookBody({
  role,
  candidate,
  boardEmail,
  onConfirm,
  onCancel,
}: {
  role: RoleBoard;
  candidate: SchedulingCandidate;
  boardEmail: string;
  onConfirm: (payload: BookPayload, chainLabel: string) => void;
  onCancel: () => void;
}) {
  const chains = role.finalRounds.chains;
  const hasHm = Boolean(role.finalRounds.config?.hiringManager);
  const [pickedStart, setPickedStart] = useState<string>(chains[0]?.start || '');
  const [mode, setMode] = useState<FinalRoundMode>('onsite');
  const [contactPhone, setContactPhone] = useState('');
  const [contactWho, setContactWho] = useState<'recruiter' | 'hiring-manager'>(
    role.finalRounds.config?.onsiteContactWho === 'hiring-manager' && hasHm ? 'hiring-manager' : 'recruiter'
  );
  // Book a meeting room by default — an onsite round needs one; a free room is
  // reserved automatically at booking time.
  const [bookRoom, setBookRoom] = useState(true);
  const [remote, setRemote] = useState<string[]>([]);
  const [audience, setAudience] = useState<PanelAudience>({
    recipients: panelPeople(role.finalRounds.config),
    channel: '',
  });

  const chain: ChainOption | undefined = chains.find((c) => c.start === pickedStart);
  const interviewers = [...new Set((chain?.segments || []).flatMap((s) => s.participants))];

  // Debrief options for the picked chain. Re-fetched when the chain changes —
  // "after the round" means something different for each option.
  const [debriefs, setDebriefs] = useState<DebriefOption[] | null>(null);
  const [debriefStart, setDebriefStart] = useState('');
  const [debriefError, setDebriefError] = useState<string | null>(null);
  // 15 by default (TA's standard); 30 and 45 for the rounds that need longer.
  // Changing it re-grades the options — a 45-minute debrief clashes with things
  // a 15-minute one fits between.
  const [debriefMinutes, setDebriefMinutes] = useState(15);

  /* eslint-disable react-hooks/set-state-in-effect -- synchronizes with the
     proxy's calendar read; every setState runs in an async continuation. */
  useEffect(() => {
    if (!chain) return;
    let live = true;
    setDebriefs(null);
    setDebriefStart('');
    setDebriefError(null);
    fetchDebriefOptions({
      postingId: role.postingId,
      boardEmail,
      chainEnd: chain.end,
      minutes: debriefMinutes,
    })
      .then((res) => {
        if (!live) return;
        if (res.error) {
          setDebriefError(res.error);
          return;
        }
        setDebriefs(res.options || []);
        // Pre-select the first option, which is the soonest FREE one when any
        // exists — the same ranking the list is shown in.
        setDebriefStart(res.options?.[0]?.start || '');
      })
      .catch(() => live && setDebriefError('Could not read the calendars for a debrief slot.'));
    return () => {
      live = false;
    };
  }, [chain?.end, role.postingId, boardEmail, debriefMinutes]); // eslint-disable-line react-hooks/exhaustive-deps
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid =
    !!chain &&
    (mode === 'remote' || contactPhone.trim().length > 0) &&
    audienceReady(audience) &&
    // A debrief the recruiter has not confirmed is exactly what this replaced.
    // If the lookup failed, the server's own auto-pick still applies.
    (!!debriefStart || !!debriefError);

  const toggleRemote = (email: string) =>
    setRemote((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  return (
    <>
      <ModalHeader>
        <ModalTitle>Book final round — {candidate.name}</ModalTitle>
      </ModalHeader>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-gray-700">Confirmed final round</span>
          {chains.length === 0 ? (
            <p className="text-sm text-gray-500">
              No solved chain available — re-check the round template and calendars first.
            </p>
          ) : (
            <RadioGroup value={pickedStart} onValueChange={setPickedStart}>
              {chains.map((c, i) => (
                <RadioItem
                  key={c.start}
                  value={c.start}
                  label={`${slotNumber(i)} ${fmtSlotRange(c)} — ${c.segments
                    .map((s) => `${s.breakBefore ? `${s.gapBefore}min break · ` : ''}${fmtTime(s.start)} ${s.label}`)
                    .join(' → ')}`}
                />
              ))}
            </RadioGroup>
          )}
        </div>

        <DebriefPicker
          options={debriefs}
          error={debriefError}
          value={debriefStart}
          minutes={debriefMinutes}
          onChange={setDebriefStart}
          onMinutesChange={setDebriefMinutes}
        />

        <Select value={mode} onValueChange={(v) => setMode(v as FinalRoundMode)}>
          <SelectTrigger label="Onsite or remote" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="onsite">Onsite — one room for the whole final round</SelectItem>
            <SelectItem value="remote">Remote — Meet link per segment</SelectItem>
          </SelectContent>
        </Select>

        {mode === 'onsite' && (
          <div className="space-y-3 rounded-xl border border-gray-200 p-3">
            <Select value={contactWho} onValueChange={(v) => setContactWho(v as typeof contactWho)}>
              <SelectTrigger label="Who collects the candidate" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recruiter">You (recruiter shares their own number)</SelectItem>
                <SelectItem value="hiring-manager" disabled={!hasHm}>
                  Hiring manager{hasHm ? '' : ' — add one to the round template first'}
                </SelectItem>
              </SelectContent>
            </Select>
            <Input
              label="Contact phone for the candidate (they call this on arrival)"
              value={contactPhone}
              onChange={(e) => setContactPhone((e.target as HTMLInputElement).value)}
            />
            <Checkbox
              label="Book a meeting room (a free one is reserved automatically)"
              labelClassName="text-sm text-gray-700"
              checked={bookRoom}
              onCheckedChange={(v) => setBookRoom(v === true)}
            />
            <div className="space-y-1.5">
              <span className="block text-sm font-medium text-gray-700">
                Anyone remote that day? Their segment gets a Meet link for the room.
              </span>
              {interviewers.map((email) => (
                <Checkbox
                  key={email}
                  label={fullNameFromEmail(email)}
                  labelClassName="text-sm text-gray-700"
                  checked={remote.includes(email)}
                  onCheckedChange={() => toggleRemote(email)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1.5 rounded-xl border border-gray-200 p-3">
          <span className="block text-sm font-medium text-gray-700">Final agenda goes to</span>
          <PanelRecipientsField
            id="fr-agenda-audience"
            suggested={panelPeople(role.finalRounds.config)}
            value={audience}
            onChange={setAudience}
          />
          <p className="text-xs text-gray-500">
            The full timeline plus the debrief
            {mode === 'onsite' ? ', with a quick office-vs-remote check-in' : ''}. Any meeting that has
            to move for the debrief is named in it.
          </p>
        </div>

        {/* What the button actually does. Kept verbatim from before the debrief
            picker moved this body into its own file — a cascade this wide should
            never be a surprise. */}
        <p className="text-xs text-gray-500">
          Booking creates a calendar event per participant ({interviewers.map(firstNameFromEmail).join(', ') || '—'}),
          {mode === 'onsite'
            ? ' one continuous room block for the candidate,'
            : ' one single meeting for the candidate with the agenda and one Meet link,'}{' '}
          a 15-min debrief with the interviewers + you (no get-to-knows), and releases any tentative
          holds. Every title is prefixed {mode === 'onsite' ? '[Onsite]' : '[Online]'}. The candidate
          summary opens right after, ready to send.
        </p>
      </div>
      <ModalActions
        confirmLabel="Book final round"
        confirmDisabled={!valid}
        onConfirm={() => {
          if (!chain) return;
          onConfirm(
            {
              mode,
              chain: { segments: chain.segments.map((s) => ({ key: s.key, start: s.start })) },
              contactPhone: mode === 'onsite' ? contactPhone.trim() : undefined,
              contactWho: mode === 'onsite' ? contactWho : undefined,
              bookRoom: mode === 'onsite' ? bookRoom : undefined,
              remoteParticipants: mode === 'onsite' ? remote : undefined,
              channel: audience.channel || undefined,
              agendaRecipients: audience.channel ? undefined : audience.recipients,
              debriefStart: debriefStart || undefined,
              debriefMinutes,
            },
            fmtSlotRange(chain)
          );
        }}
        onCancel={onCancel}
      />
    </>
  );
}

/**
 * The 15-minute debrief, confirmed rather than assumed.
 *
 * Needs-move options are offered too, with the clash named: the debrief matters
 * more than a clean calendar, and hiding those would leave a recruiter thinking
 * there is no slot at all.
 */
function DebriefPicker({
  options,
  error,
  value,
  minutes,
  onChange,
  onMinutesChange,
}: {
  options: DebriefOption[] | null;
  error: string | null;
  value: string;
  minutes: number;
  onChange: (start: string) => void;
  onMinutesChange: (minutes: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="block text-sm font-medium text-gray-700">Debrief (interviewers + you)</span>
        <div className="flex items-center gap-1">
          {[15, 30, 45].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onMinutesChange(m)}
              className={`rounded-full px-2.5 py-1 text-xs ${
                m === minutes ? 'bg-gray-900 font-semibold text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {m} min
            </button>
          ))}
        </div>
      </div>
      {error ? (
        <p className="text-xs text-gray-500">
          {error} Booking still goes ahead — the board picks the first free slot, as before.
        </p>
      ) : options === null ? (
        <p className="flex items-center gap-2 text-xs text-gray-500">
          <Spinner className="h-3 w-3" /> Looking for a slot everyone can make…
        </p>
      ) : options.length === 0 ? (
        <p className="text-xs text-gray-500">
          No slot found in the days after the round — the board will place one and the invite will show
          the clash.
        </p>
      ) : (
        <RadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <RadioItem
              key={option.start}
              value={option.start}
              label={
                `${fmtSlotRange(option)}` +
                (option.conflicts.length > 0
                  ? ` — ${option.conflicts
                      .map((c) => `${firstNameFromEmail(c.email)} moves “${c.title}”`)
                      .join('; ')}`
                  : '')
              }
            />
          ))}
        </RadioGroup>
      )}
      {options?.some((o) => o.start === value && o.grade === 'needs-move') && (
        <p className="text-xs" style={{ color: COLORS.orangeText }}>
          Someone has to move a meeting for this slot — it is named in the agenda message so they know.
        </p>
      )}
    </div>
  );
}
